import React, { useEffect, useRef, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { notifyError } from '../../../ToastMessages';
import FormError from '../../../FormError';
import Spinner from '../shared/Spinner';
import LoadFailure from '../shared/LoadFailure';
import { apiFetch } from '../shared/api';
import BusinessSearch from './BusinessSearch';
import SelectedBusiness from './SelectedBusiness';
import BusinessDetailsFields from './BusinessDetailsFields';
import ManualBusinessFields from './ManualBusinessFields';
import LogoPicker from './LogoPicker';
import NotesField from './NotesField';
import SupportLine from './SupportLine';
import DuplicateNotice from './DuplicateNotice';

const EMPTY_LOGO = { candidateUrl: null, file: null };
const EMPTY_MANUAL = {
    slug: '',
    name: '',
    address: '',
    phone: '',
    url: '',
    display_url_as: '',
    industry_id: '',
    show_address: false,
};
const OVERRIDE_FIELDS = ['name', 'address', 'phone', 'url', 'display_url_as'];
const FIELD_BOUND_ERRORS = ['place_id', 'google_place_id', 'name', 'slug', 'address', 'phone', 'url', 'display_url_as', 'industry_id'];

export function hostFromUrl(value) {
    if (!value) {
        return '';
    }
    try {
        return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`).hostname;
    } catch (error) {
        return value;
    }
}

function appendIfNonEmpty(formData, key, value) {
    if (value && value.trim() !== '') {
        formData.append(key, value);
    }
}

function buildFormData({ manualMode, selection, detailsValues, detailsSnapshot, manual, note, logo, allowDuplicate }) {
    const formData = new FormData();

    if (manualMode) {
        formData.append('mode', 'manual');
        formData.append('name', manual.name);
        formData.append('slug', manual.slug);
        appendIfNonEmpty(formData, 'address', manual.address);
        appendIfNonEmpty(formData, 'phone', manual.phone);
        appendIfNonEmpty(formData, 'url', manual.url);
        appendIfNonEmpty(formData, 'display_url_as', manual.display_url_as);
        if (manual.industry_id) {
            formData.append('industry_id', manual.industry_id);
        }
        formData.append('show_address', manual.show_address ? '1' : '0');
        appendIfNonEmpty(formData, 'note', note);
        if (logo.file) {
            formData.append('logo', logo.file);
        }
        return formData;
    }

    formData.append('mode', 'place');
    formData.append('place_id', selection.place_id);
    if (allowDuplicate) {
        formData.append('allow_duplicate', '1');
    }
    if (detailsValues.industry_id) {
        formData.append('industry_id', detailsValues.industry_id);
    }
    appendIfNonEmpty(formData, 'note', note);
    if (logo.file) {
        formData.append('logo', logo.file);
    } else if (logo.candidateUrl) {
        formData.append('logo_url', logo.candidateUrl);
    }
    OVERRIDE_FIELDS.forEach((field) => {
        const current = (detailsValues[field] || '').trim();
        const original = (detailsSnapshot[field] || '').trim();
        if (current !== original) {
            formData.append(field, detailsValues[field]);
        }
    });

    return formData;
}

export default function DemoAccountAdd({
    abilities,
    industries,
    endpoints,
    mapsApiKey,
    supportUrl,
    supportPhone,
    brandedLinkBase,
    limits,
    googleImageSearchBase,
}) {
    const [manualMode, setManualMode] = useState(false);
    const [selection, setSelection] = useState(null);
    const [detailsValues, setDetailsValues] = useState({});
    const [manual, setManual] = useState(EMPTY_MANUAL);
    const [logo, setLogo] = useState(EMPTY_LOGO);
    const [note, setNote] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [duplicates, setDuplicates] = useState(null);
    const [duplicatesLookup, setDuplicatesLookup] = useState('idle');
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
    const [serverRequiresDuplicateConfirm, setServerRequiresDuplicateConfirm] = useState(false);
    const detailsSnapshotRef = useRef({});
    const lookupRequestRef = useRef(0);
    const manualContainerRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (manualMode && manualContainerRef.current) {
            const firstField = manualContainerRef.current.querySelector('input, select, textarea');
            if (firstField) {
                firstField.focus();
            }
        }
    }, [manualMode]);

    const resetDuplicates = () => {
        lookupRequestRef.current += 1;
        setDuplicates(null);
        setDuplicatesLookup('idle');
        setDuplicateConfirmed(false);
        setServerRequiresDuplicateConfirm(false);
    };

    const lookupDuplicates = async (placeId) => {
        lookupRequestRef.current += 1;
        const requestId = lookupRequestRef.current;
        setDuplicates(null);
        setDuplicatesLookup('loading');
        setDuplicateConfirmed(false);

        try {
            const { status, json } = await apiFetch(`${endpoints.placeDuplicates}?place_id=${encodeURIComponent(placeId)}`);
            if (requestId !== lookupRequestRef.current) {
                return;
            }
            if (status >= 200 && status < 300 && json?.data) {
                setDuplicates(json.data);
                setDuplicatesLookup('idle');
                return;
            }
            console.error(`Duplicate demo account lookup failed with status ${status}`);
            setDuplicatesLookup('failed');
        } catch (error) {
            if (requestId !== lookupRequestRef.current) {
                return;
            }
            console.error(error);
            setDuplicatesLookup('failed');
        }
    };

    const handleSelect = (details) => {
        const initialDetails = {
            name: details.name || '',
            address: details.address || '',
            phone: details.phone || '',
            url: details.website || '',
            display_url_as: details.display_url || '',
            industry_id: details.industry_id ? String(details.industry_id) : '',
        };
        setManualMode(false);
        setServerRequiresDuplicateConfirm(false);
        setSelection(details);
        setDetailsValues(initialDetails);
        detailsSnapshotRef.current = initialDetails;
        setLogo(EMPTY_LOGO);
        setErrors({});
        lookupDuplicates(details.place_id);
    };

    const handleChangeSelection = () => {
        resetDuplicates();
        setSelection(null);
        setDetailsValues({});
        detailsSnapshotRef.current = {};
        setLogo(EMPTY_LOGO);
        setErrors({});
        searchRef.current?.focus();
    };

    const handleManualToggle = (checked) => {
        setManualMode(checked);
        setErrors({});
        if (checked) {
            resetDuplicates();
        }
        setLogo(EMPTY_LOGO);
        if (checked) {
            setSelection(null);
            setDetailsValues({});
            detailsSnapshotRef.current = {};
        }
    };

    const handleDetailsChange = (field, value) => {
        setDetailsValues((values) => ({ ...values, [field]: value }));
    };

    const handleManualChange = (field, value) => {
        setManual((values) => ({ ...values, [field]: value }));
    };

    const handleCreate = async () => {
        setSubmitting(true);

        try {
            const { status, json } = await apiFetch(endpoints.store, {
                method: 'POST',
                body: buildFormData({ manualMode, selection, detailsValues, detailsSnapshot: detailsSnapshotRef.current, manual, note, logo, allowDuplicate: duplicateNoticeShown && duplicateConfirmed }),
            });

            if (status === 201) {
                window.location.assign(json.data.redirect_to);
                return;
            }

            if (status === 422) {
                const validationErrors = json?.errors && typeof json.errors === 'object' && Object.keys(json.errors).length > 0 ? json.errors : null;
                setErrors(validationErrors || {});
                if (validationErrors?.allow_duplicate && !manualMode && selection) {
                    setServerRequiresDuplicateConfirm(true);
                    lookupDuplicates(selection.place_id);
                }
                if (!validationErrors) {
                    notifyError(json?.message || __('Please check the form and try again.'));
                }
            } else if (status === 403) {
                notifyError(json?.error?.message || json?.message || __('You are not allowed to do that.'));
            } else {
                notifyError(__('Something went wrong (:status). Please try again.', { ':status': status }));
            }
            setSubmitting(false);
        } catch (error) {
            console.error(error);
            notifyError(__('Something went wrong. Please try again.'));
            setSubmitting(false);
        }
    };

    const duplicateNoticeShown = !manualMode && Boolean(selection)
        && (duplicates?.own?.length > 0 || duplicates?.others?.length > 0 || serverRequiresDuplicateConfirm);
    const placeDomain = selection ? (selection.display_url || hostFromUrl(selection.website)) : '';
    const manualDomain = manual.display_url_as.trim() || hostFromUrl(manual.url);
    const canSubmit = manualMode
        ? manual.name.trim() !== '' && manual.slug.trim() !== ''
        : Boolean(selection) && Boolean(detailsValues.industry_id) && (!duplicateNoticeShown || duplicateConfirmed);
    const showLogoAndNotes = Boolean(selection) || manualMode;
    const unboundErrors = Object.entries(errors)
        .filter(([field]) => !FIELD_BOUND_ERRORS.includes(field))
        .map(([field, messages]) => [field, Array.isArray(messages) ? messages[0] : String(messages)]);

    if (!abilities.create) {
        return (
            <div className="demo-account-add demo-island">
                <div className="alert alert-warning" role="alert" data-testid="demo-add-forbidden">
                    {__('Your account cannot create demo accounts.')}
                </div>
            </div>
        );
    }

    return (
        <div className="demo-account-add card demo-island">
            <div className="card-body">
                <p className="text-muted mb-4">{__('This will create a demo company for a business you want to present to and generate a reputation scorecard.')}</p>
                {unboundErrors.length > 0 &&
                    <div data-testid="demo-add-form-errors">
                        {unboundErrors.map(([field, message]) => <FormError key={field} error_message={message} />)}
                    </div>
                }

                <BusinessSearch
                    ref={searchRef}
                    endpoints={endpoints}
                    mapsApiKey={mapsApiKey}
                    onSelect={handleSelect}
                    onLoadingChange={setDetailsLoading}
                    showInlineSpinner={Boolean(selection)}
                    disabled={submitting}
                />

                {abilities.createManual &&
                    <div className="custom-control custom-checkbox mb-3">
                        <input
                            id="demo-add-manual-toggle"
                            data-testid="demo-add-manual-toggle"
                            type="checkbox"
                            className="custom-control-input"
                            checked={manualMode}
                            onChange={(event) => handleManualToggle(event.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="demo-add-manual-toggle">
                            {__('Manually enter business')}
                        </label>
                    </div>
                }

                {!selection && !manualMode && detailsLoading &&
                    <Spinner block label={__('Loading business details')} />
                }

                {selection &&
                    <SelectedBusiness
                        selection={selection}
                        onChange={handleChangeSelection}
                        error={(errors.place_id || errors.google_place_id || [])[0]}
                    />
                }

                {selection && duplicateNoticeShown &&
                    <DuplicateNotice
                        own={duplicates?.own}
                        others={duplicates?.others}
                        confirmed={duplicateConfirmed}
                        onConfirmChange={setDuplicateConfirmed}
                    />
                }

                {selection && duplicatesLookup === 'loading' &&
                    <p className="text-muted small" data-testid="demo-add-duplicate-lookup-loading">
                        <Spinner label={__('Checking for existing demo accounts')} className="mr-2" />
                        {__('Checking for existing demo accounts...')}
                    </p>
                }

                {selection && duplicatesLookup === 'failed' && !duplicateNoticeShown &&
                    <div data-testid="demo-add-duplicate-lookup-failed">
                        <LoadFailure
                            message={__('We could not check whether this business already has a demo account. You can still create it.')}
                            onRetry={() => lookupDuplicates(selection.place_id)}
                            className="mb-2"
                        />
                    </div>
                }

                {selection &&
                    <BusinessDetailsFields
                        values={detailsValues}
                        onChange={handleDetailsChange}
                        errors={errors}
                        industries={industries}
                        canOverride={abilities.overrideBusinessDetails}
                    />
                }

                {abilities.createManual && manualMode &&
                    <div ref={manualContainerRef}>
                        <ManualBusinessFields
                            values={manual}
                            onChange={handleManualChange}
                            errors={errors}
                            industries={industries}
                            brandedLinkBase={brandedLinkBase}
                            slugLimit={limits.slug}
                            slugCheckUrl={endpoints.slugCheck}
                        />
                    </div>
                }

                {showLogoAndNotes &&
                    <>
                        <hr />
                        <LogoPicker
                            endpoints={endpoints}
                            domain={manualMode ? manualDomain : placeDomain}
                            businessName={manualMode ? manual.name : (selection ? selection.name : '')}
                            googleImageSearchBase={googleImageSearchBase}
                            value={logo}
                            onChange={setLogo}
                        />
                    </>
                }

                <SupportLine supportUrl={supportUrl} supportPhone={supportPhone} />

                {showLogoAndNotes &&
                    <NotesField value={note} onChange={setNote} max={limits.note} />
                }

                {showLogoAndNotes &&
                    <div className="demo-add-create-wrapper text-center mt-3">
                        <button
                            type="button"
                            className="btn btn-primary btn-lg"
                            data-testid="demo-add-create"
                            disabled={!canSubmit || submitting}
                            onClick={handleCreate}
                        >
                            {submitting
                                ? <Spinner label={__('Creating demo account')} className="mr-2" />
                                : <i className="fas fa-user-plus mr-2" aria-hidden="true" />}
                            {__('Create Demo Account')}
                        </button>
                    </div>
                }

                <Toaster position="bottom-center" />
            </div>
        </div>
    );
}
